// Système de PARTICULES d'action — purement cosmétique : jamais dans la simulation, le hash ou le
// rejeu réseau. Les bursts sont émis sur des TRANSITIONS d'état détectées entre `prev` et `cur`
// (atterrissage, or ramassé, creusage), une seule fois par tick ; chaque particule suit ensuite une
// trajectoire balistique PURE de son âge (aucune horloge murale, aucun Math.random en boucle de
// rendu -> pas de scintillement). Dessiné en espace MONDE (sous la caméra), par-dessus les entités.
import { dashVariant } from './dash-variant'
import { creusageDuLoot, effetDuCreusage } from './creusage'
import { equipped, trailDashColors, trailDashGlyphe, trailDigColors, trailDigGlyphe, type TrailGlyphe } from './loadout'
import type { GameState } from '../shared/state'
import { SUB } from '../shared/types'

const TS = 48
const PXSUB = TS / SUB // 6 px par sous-unité (miroir de PX_PER_SUB de render.ts)

export type BurstKind = 'dust' | 'spark' | 'debris' | 'crystal' | 'shatter'
// `trail` (Lot C T5) = origine COSMÉTIQUE d'un burst re-teintable par un loot équipé : 'dash' (la
// traînée du coureur SUIVI) ou 'dig' (les gravats de creusage). Absent = burst non teinté (défaut).
export interface Burst { x: number; y: number; kind: BurstKind; trail?: 'dash' | 'dig' }

/**
 * PURE : bursts à émettre CE tick, dérivés des transitions entre `prev` et `cur` (positions en px,
 * espace monde). Sans `prev` (première frame), rien. Aucun effet de bord -> testable hors navigateur.
 * `focusIdx` = coureur LOCAL (solo 0 / online youAre) : seule sa traînée de dash porte le tag
 * `trail:'dash'` (les effets sont SOLO ; on ne teinte pas le dash des autres avec sa propre
 * loadout). Le tag n'ajoute/ne retire jamais un burst -> `kind`/longueur/position inchangés.
 */
export function collectBursts(prev: GameState | null, cur: GameState, focusIdx = 0): Burst[] {
  if (!prev) return []
  const cols = cur.cols
  const cx = (cell: number) => (cell % cols) * TS + TS / 2
  const cy = (cell: number) => Math.floor(cell / cols) * TS + TS / 2
  const out: Burst[] = []
  cur.runners.forEach((r, i) => {
    const p = prev.runners[i]
    if (r.alive && p && p.falling && !r.falling) out.push({ x: r.x * PXSUB + TS / 2, y: r.y * PXSUB + TS, kind: 'dust' }) // atterrissage (PAS une traînée : non teinté)
    // DASH (18/07, variantes aléatoires) : le pavé d'éclats n'appartient qu'au CRISTAL (0) ;
    // la gerbe de fin change de matière selon la variante (glace / poussière / étincelles).
    const dv = dashVariant(r.dashUntil, i)
    const trail = i === focusIdx ? 'dash' as const : undefined // seul le coureur suivi porte la traînée cosmétique
    if (r.alive && r.dashUntil > cur.tick && cur.tick >= r.dashChargeUntil && dv === 0) out.push({ x: r.x * PXSUB + TS / 2, y: r.y * PXSUB + TS / 2 + 6, kind: 'crystal', trail })
    if (r.alive && p && p.dashUntil > prev.tick && prev.tick >= p.dashChargeUntil && r.dashUntil <= cur.tick) {
      // Gerbe de fin par variante (3 designs 22/07) : 0 Comète -> éclats de glace ; 1 Arcane ->
      // pop d'étincelles ; 2 Onde tonnerre -> dérapage complet (étincelles + poussière).
      if (dv === 0) out.push({ x: r.x * PXSUB + TS / 2, y: r.y * PXSUB + TS / 2, kind: 'shatter', trail })
      else if (dv === 1) out.push({ x: r.x * PXSUB + TS / 2, y: r.y * PXSUB + TS / 2, kind: 'spark', trail })
      else {
        out.push({ x: r.x * PXSUB + TS / 2, y: r.y * PXSUB + TS / 2, kind: 'spark', trail })
        out.push({ x: r.x * PXSUB + TS / 2, y: r.y * PXSUB + TS - 4, kind: 'dust' })
      }
    }
  })
  const still = new Set(cur.golds)
  for (const cell of prev.golds) if (!still.has(cell)) out.push({ x: cx(cell), y: cy(cell), kind: 'spark' }) // or ramassé (non teinté)
  const before = new Set(prev.holes.map((h) => h.cell))
  for (const h of cur.holes) if (!before.has(h.cell)) {
    // CREUSAGE (14/08, « je veux tout voir ») : la brique qui cède ne crachait QUE des gravats.
    // Trois couches désormais, comme le coup de maillet du 05/08 — et pour la même raison : un
    // impact se lit à la somme de ses effets, jamais à un seul.
    //   · les GRAVATS, teintables, qui partent en éventail (existant) ;
    //   · les ÉTINCELLES du fer sur la pierre, au point de contact, un peu au-dessus du trou ;
    //   · la POUSSIÈRE qui retombe, émise plus bas, là où les débris s'écrasent.
    out.push({ x: cx(h.cell), y: cy(h.cell), kind: 'debris', trail: 'dig' })
    out.push({ x: cx(h.cell), y: cy(h.cell) - 6, kind: 'spark' })
    out.push({ x: cx(h.cell), y: cy(h.cell) + 8, kind: 'dust' })
  }
  // Caisse qui ÉCLATE (audit 16/07) : l'or et le creusage avaient leurs éclats, la casse d'une caisse
  // n'avait RIEN — ni son ni particules, alors que c'est un geste rare et décisif (elle libère l'or).
  // On lit l'event plutôt que la disparition d'une caisse : lui seul distingue la CASSE d'un simple
  // déplacement, et il porte la case exacte (casse par chute, par bombe ou par lave).
  for (const e of cur.events) if (e.type === 'crateBreak') out.push({ x: cx(e.cell), y: cy(e.cell), kind: 'debris' })
  // COUP DE MAILLET ENCAISSÉ (05/08, retour Claude-Alain « on ne comprend pas ce qu'il se
  // passe ») : l'impact ÉCLATE — double gerbe d'étincelles + poussière sur la case touchée.
  // Avec la traînée du swing, la vrille de la victime et la secousse, le coup se lit en une frame.
  for (const e of cur.events) if (e.type === 'malletHit') {
    out.push({ x: cx(e.cell), y: cy(e.cell), kind: 'spark' })
    out.push({ x: cx(e.cell) + 6, y: cy(e.cell) - 5, kind: 'spark' })
    out.push({ x: cx(e.cell), y: cy(e.cell) + 8, kind: 'dust' })
  }
  // LE PAS DU PORTEUR DE MAILLET (06/08) : une pincée de poussière sous le pied à chaque case.
  // C'est le troisième étage du poids, avec la posture penchée et le clonk : on le voit peser.
  for (const e of cur.events) {
    if (e.type !== 'malletPas') continue
    const r = cur.runners[e.runner]
    if (r) out.push({ x: r.x * PXSUB + TS / 2, y: r.y * PXSUB + TS, kind: 'dust' })
  }
  // ATTERRISSAGE D'UN VOL PLANÉ (05/08) : la victime vrillée touche terre -> pouf de poussière.
  // Le stun distingue un coup encaissé d'un bond volontaire (sauteur, crapaud, géant) ; un
  // coureur HUMAIN en vol est toujours une victime (bousculade du géant comprise, sans stun).
  cur.runners.forEach((r, i) => {
    const p = prev.runners[i]
    if (!p || !(p.hop > 0 && r.hop === 0)) return
    const humain = !r.crapaud && r.geantUntil <= cur.tick
    if (humain || r.stunUntil > cur.tick) out.push({ x: r.x * PXSUB + TS / 2, y: r.y * PXSUB + TS, kind: 'dust' })
  })
  // `?? []` : les vieux tests de bursts fabriquent des états PARTIELS sans moines — on ne
  // casse pas quatorze tests d'un coup pour un pouf de poussière.
  ;(cur.monks ?? []).forEach((m, i) => {
    const p = prev.monks?.[i]
    if (p && p.hop > 0 && m.hop === 0 && m.stunUntil > cur.tick) out.push({ x: m.x * PXSUB + TS / 2, y: m.y * PXSUB + TS, kind: 'dust' })
  })
  return out
}

interface P { x: number; y: number; vx: number; vy: number; born: number; life: number; kind: BurstKind; tint?: string; glyphe?: TrailGlyphe }
// Réglage par nature : nb de particules, durée (ticks), vitesse, gravité (px/tick²), couleur, taille.
const SPEC: Record<BurstKind, { n: number; life: number; spd: number; g: number; col: string; size: number }> = {
  dust: { n: 6, life: 11, spd: 1.7, g: 0.1, col: '226,216,194', size: 3.4 },
  spark: { n: 9, life: 15, spd: 2.5, g: 0.05, col: '255,224,120', size: 2.6 },
  debris: { n: 8, life: 17, spd: 2.3, g: 0.17, col: '154,112,80', size: 3.2 },
  crystal: { n: 1, life: 16, spd: 0.3, g: -0.02, col: '106,252,255', size: 2.5 },   // éclat posé sur le trajet (flotte à peine)
  shatter: { n: 10, life: 14, spd: 2.6, g: 0.12, col: '106,252,255', size: 2.5 },   // gerbe de fin de dash
}
const buf: P[] = []
let lastTick = -1

/**
 * Les particules d'UN burst, prêtes à vivre (born = 0, l'appelant le pose).
 * Extraite de `spawn` pour que la planche cosmetiques2 fabrique EXACTEMENT les
 * mêmes particules que le jeu : même éventail, mêmes vitesses, même teinture.
 */
/**
 * CE QUE LA BRIQUE CRACHE, selon la façon de creuser (13/08). Troisième étage de la règle apprise
 * sur les frappes de maillet : des poses distinctes et des courbes distinctes ne suffisent pas —
 * sans effet propre, quatre gestes « font 4× la même chose ».
 *   · éclats  (le tailleur)  : beaucoup de petits morceaux, vifs et très ouverts sur les côtés ;
 *   · blocs   (le bûcheron)  : peu de morceaux, gros et lents, qui retombent lourdement ;
 *   · poudre  (la vibreuse)  : un nuage fin, dense, qui ne va pas loin et s'éteint vite ;
 *   · lueur   (le sourcier)  : la matière ne tombe pas, elle MONTE et s'attarde.
 */
/** L'effet porté par une pioche équipée, ou null si elle n'a pas de style. */
function effetDuLootPioche(lootId: string | null | undefined): string | null {
  const c = creusageDuLoot(lootId)
  return c === null ? null : effetDuCreusage(c)
}

const MOD_CREUSAGE: Record<string, { n: number; spd: number; life: number; monte: number; ouvre: number }> = {
  eclats: { n: 1.6, spd: 1.25, life: 0.8, monte: 0, ouvre: 1.25 },
  blocs: { n: 0.5, spd: 0.7, life: 1.35, monte: 0, ouvre: 0.6 },
  poudre: { n: 2.2, spd: 0.55, life: 0.55, monte: 0.15, ouvre: 1 },
  lueur: { n: 1.1, spd: 0.6, life: 1.8, monte: 1.5, ouvre: 0.75 },
}

export function particulesDeBurst(b: Burst, cols: string[], glyphe: TrailGlyphe | null, effet: string | null = null): P[] {
  const s = SPEC[b.kind]
  const m = (effet && MOD_CREUSAGE[effet]) || null
  const n = Math.max(1, Math.round(s.n * (m?.n ?? 1)))
  const out: P[] = []
  for (let i = 0; i < n; i++) {
    const a = n === 1 ? Math.PI * 1.5 : Math.PI + (i / (n - 1)) * Math.PI // demi-cercle vers le HAUT, éventail déterministe (n=1 : plein haut)
    const sp = s.spd * (m?.spd ?? 1) * (0.55 + 0.45 * (((i * 7) % 5) / 4))   // vitesses variées mais fixes (pas d'aléa)
    // `ouvre` écarte ou resserre l'éventail ; `monte` inverse la gravité apparente (le sourcier).
    const vx = Math.cos(a) * sp * (m?.ouvre ?? 1)
    const vy = Math.sin(a) * sp - 0.4 - (m?.monte ?? 0) * 1.2
    out.push({ x: b.x, y: b.y, vx, vy, born: 0, life: Math.round(s.life * (m?.life ?? 1)), kind: b.kind,
      tint: cols.length ? cols[i % cols.length] : undefined, glyphe: glyphe ?? undefined })
  }
  return out
}

function spawn(b: Burst, tick: number): void {
  // Teinture COSMÉTIQUE (Lot C T5) : un burst « traînée » lit le loot équipé du bon slot et prend
  // sa palette (vide = pas de loot -> aucune teinte, comportement par défaut byte-identique).
  // Depuis la vague du 05/08, le loot peut aussi changer la FORME (glyphe : note, papillon, confetti).
  const cols = b.trail === 'dash' ? trailDashColors(equipped('trailDash'))
    : b.trail === 'dig' ? trailDigColors(equipped('trailDig')) : []
  const glyphe = b.trail === 'dash' ? trailDashGlyphe(equipped('trailDash'))
    : b.trail === 'dig' ? trailDigGlyphe(equipped('trailDig')) : null
  // L'effet ne s'applique qu'aux GRAVATS de creusage, et seulement si une pioche à style est
  // équipée : sans elle, le comportement reste identique au byte près.
  const effet = b.trail === 'dig' ? effetDuLootPioche(equipped('pickaxe')) : null
  for (const p of particulesDeBurst(b, cols, glyphe, effet)) { p.born = tick; buf.push(p) }
}

/**
 * Émet (au CHANGEMENT de tick seulement, pour ne pas ré-émettre à chaque frame d'interpolation) puis
 * dessine les particules vivantes. `t` = cur.tick + alpha. Purement cosmétique (jamais hash/rejeu).
 */
export function drawParticles(ctx: CanvasRenderingContext2D, prev: GameState | null, cur: GameState, t: number, focusIdx = 0): void {
  if (cur.tick < lastTick) buf.length = 0 // nouvelle partie (tick remis à zéro) : on repart propre
  if (cur.tick !== lastTick) { for (const b of collectBursts(prev, cur, focusIdx)) spawn(b, cur.tick); lastTick = cur.tick }
  for (let k = buf.length - 1; k >= 0; k--) if (t - (buf[k]?.born ?? 0) >= (buf[k]?.life ?? 0)) buf.splice(k, 1)
  if (buf.length > 400) buf.splice(0, buf.length - 400) // filet anti-emballement
  ctx.save()
  for (const p of buf) {
    const age = t - p.born
    if (age < 0) continue
    peintParticule(ctx, p, age)
  }
  ctx.restore()
}

/** Peint UNE particule à son âge (en ticks). Partagée entre `drawParticles` et
 *  la démo de la planche cosmetiques2 : c'est la garantie que la planche montre
 *  le trait du jeu, pas une imitation. Balistique incluse. */
function peintParticule(ctx: CanvasRenderingContext2D, p: P, age: number): void {
  const s = SPEC[p.kind], k = age / p.life
  const x = p.x + p.vx * age, y = p.y + p.vy * age + 0.5 * s.g * age * age
  ctx.globalAlpha = Math.max(0, 1 - k)
  if (p.glyphe && p.tint) {   // vague du 05/08 : le loot change la FORME, la palette donne la couleur
    peintGlyphe(ctx, x, y, p.glyphe, p.tint, age)
  } else if (p.tint) { // traînée / gravats teintés par un loot équipé (Lot C T5) : gravats = carrés, dash = croix scintillante
    ctx.fillStyle = p.tint
    const rx = Math.round(x), ry = Math.round(y)
    if (p.kind === 'debris') { const sz = s.size * (1 - k * 0.5); ctx.fillRect(rx - sz / 2, ry - sz / 2, sz, sz) }
    else { ctx.fillRect(rx, ry - 3, 2, 8); ctx.fillRect(rx - 3, ry, 8, 2) }
  } else if (p.kind === 'crystal' || p.kind === 'shatter') { // croix de cristal qui scintille (style maquette B)
    ctx.fillStyle = (Math.floor(age * 0.8) & 1) === 0 ? '#cfeaff' : '#6afcff'
    const rx = Math.round(x), ry = Math.round(y)
    ctx.fillRect(rx, ry - 3, 2, 8); ctx.fillRect(rx - 3, ry, 8, 2)
  } else {
    ctx.fillStyle = `rgb(${s.col})`
    ctx.beginPath(); ctx.arc(x, y, s.size * (1 - k * 0.5), 0, Math.PI * 2); ctx.fill()
  }
}

/**
 * Les trois formes de la vague du 05/08. Tout mouvement interne (battement
 * d'ailes, tournoiement) dérive de l'ÂGE et de la position — jamais d'horloge
 * murale ni de Math.random : la règle de tête de fichier vaut pour les glyphes.
 */
function peintGlyphe(ctx: CanvasRenderingContext2D, x: number, y: number, glyphe: TrailGlyphe, tint: string, age: number): void {
  const rx = Math.round(x), ry = Math.round(y)
  ctx.fillStyle = tint
  if (glyphe === 'note') {
    /* une croche : tête pleine, hampe, crochet. Elle se balance à peine. */
    ctx.save(); ctx.translate(rx, ry); ctx.rotate(Math.sin(age * .25) * .18)
    ctx.beginPath(); ctx.ellipse(-1.5, 3.2, 2.6, 1.9, -.5, 0, 7); ctx.fill()
    ctx.fillRect(.8, -5.5, 1.3, 9)
    ctx.beginPath(); ctx.moveTo(.8, -5.5); ctx.quadraticCurveTo(4.6, -4.2, 3.4, -.6)
    ctx.quadraticCurveTo(3.6, -3.4, .8, -3.2); ctx.closePath(); ctx.fill()
    ctx.restore()
  } else if (glyphe === 'papillon') {
    /* deux ailes qui BATTENT (scale X sinusoïdal) autour d'un corps sombre.
       Le battement est ce qui le distingue d'un pétale — sans lui, ce loot
       existerait déjà. */
    const bat = .25 + .75 * Math.abs(Math.sin(age * .55))
    ctx.save(); ctx.translate(rx, ry); ctx.rotate(Math.sin(age * .18) * .3)
    for (const s2 of [-1, 1]) {
      ctx.save(); ctx.scale(bat, 1)
      ctx.beginPath(); ctx.moveTo(0, 0)
      ctx.quadraticCurveTo(s2 * 6.4, -5.4, s2 * 5.2, .2)
      ctx.quadraticCurveTo(s2 * 4.6, 3.8, 0, 1.2)
      ctx.closePath(); ctx.fill()
      ctx.restore()
    }
    ctx.fillStyle = '#3a3038'
    ctx.fillRect(-.7, -2.6, 1.4, 5.6)
    ctx.restore()
  } else {
    /* un confetti : rectangle qui tournoie ET se plie (scale Y), comme un
       papier qui tombe. L'angle dérive de la position courante : deux
       confettis voisins ne tournent jamais en phase. */
    ctx.save(); ctx.translate(rx, ry)
    ctx.rotate((x * 12.9898) % 6.28 + age * .3)
    ctx.scale(1, .35 + .65 * Math.abs(Math.sin(age * .5 + y)))
    ctx.fillRect(-2.6, -1.7, 5.2, 3.4)
    ctx.restore()
  }
}

/**
 * LA DÉMO DE LA PLANCHE (cosmetiques2) : peint l'état, à l'instant `tMs`, des
 * particules émises aux instants passés `emissions` — reconstruction PURE, sans
 * tampon : la vignette est figeable (`?t=`) et rejouable à l'identique.
 * `fin: true` = gerbe de fin de dash (shatter) ; sinon 'dash' pose un éclat de
 * trajet (crystal) et 'dig' un burst de gravats (debris). Les particules
 * fabriquées et le rendu sont CEUX du jeu (particulesDeBurst / peintParticule).
 */
export function demoTrail(ctx: CanvasRenderingContext2D, tMs: number, trail: 'dash' | 'dig', lootId: string | null,
  emissions: { t: number; x: number; y: number; fin?: boolean }[]): void {
  const MS_TICK = 1000 / 30
  const cols = trail === 'dash' ? trailDashColors(lootId) : trailDigColors(lootId)
  const glyphe = trail === 'dash' ? trailDashGlyphe(lootId) : trailDigGlyphe(lootId)
  ctx.save()
  for (const e of emissions) {
    const kind: BurstKind = trail === 'dig' ? 'debris' : e.fin ? 'shatter' : 'crystal'
    const age = (tMs - e.t) / MS_TICK
    if (age < 0 || age >= SPEC[kind].life) continue
    for (const p of particulesDeBurst({ x: e.x, y: e.y, kind, trail }, cols, glyphe)) {
      peintParticule(ctx, p, age)
    }
  }
  ctx.restore()
}
